import { Component, OnDestroy, OnInit } from '@angular/core';
import { UserProfileModel } from 'src/app/core/models/user-profile.model';
import { UserProfileService } from './user-profile.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { UserProfileCreateUpdateDeleteComponent } from './user-profile-create-update-delete/user-profile-create-update-delete.component';
import { FeiColumn } from 'src/app/core/base/interfaces/fei-column.interface';
import { DataTableBase } from 'src/app/core/base/components/data-table-base/data-table-base';
import { USER_PROFILE_FILTER_SCHEMA } from './user-profile-filter/user-profile-filter.schema';
import { CrudModeEnum } from 'src/app/core/base/enum/crud-mode.enum';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Observable } from 'rxjs';
import { EstablishmentModel } from '../../../../core/models/establishment.model';
import { EstablishmentService } from '../establishment/establishment.service';
import { map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ENUM_PERMISSIONS } from '../../../../core/enums/permission.enum';
import { AuthService } from '../../../../core/services/auth.service';
import { b64ToBlob } from '../../../../core/helpers/b64ToBlob.helper';
import { ContentTypeEnum } from '../../../../core/base/enum/content-type.enum';
import { FileSaverService } from 'ngx-filesaver';
import { PhoneNumberModel } from '../../../../core/models/phone-number.model';

@Component({
  selector: 'vex-user-profile',
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.scss'],
})
export class UserProfileComponent extends DataTableBase<UserProfileModel> implements OnInit, OnDestroy {
  permission = ENUM_PERMISSIONS;

  feisColumns: Array<FeiColumn> = [
    { label: 'Nom', column: 'lastName', propertyDisplay: null, upperCase: true },
    { label: 'Prénom', column: 'firstName', propertyDisplay: null },
    { label: 'Email', column: 'email', propertyDisplay: null },
    { label: 'Téléphone', column: 'mobileNumber', propertyDisplay: null },
    { label: 'Rôles utilisateur', column: 'contactType', propertyDisplay: null },
    {
      label: '',
      column: 'actions',
      canUpdate: false,
      canArchive: this.authService.hasPermission(ENUM_PERMISSIONS.DELETE_INTERNAL_CONTACT),
      canDelete: false,
    },
  ];

  canShowArchivesPermission = false;

  protected override filterSchema = USER_PROFILE_FILTER_SCHEMA;

  constructor(
    public service: UserProfileService,
    private dialog: MatDialog,
    private establishmentService: EstablishmentService,
    private router: Router,
    public authService: AuthService,
    private fileSaver: FileSaverService,
  ) {
    super();
    this.displayedColumns = this.feisColumns.map(x => x.column);
    this.sortField = 'lastName';
    this.sortOrder = 'asc';
  }

  initData(): void {
    super.initData();
    this.filters.requiredFields = [
      'lastName',
      'firstName',
      'contactType',
      'establishments',
      'activities',
      'contactTypes',
    ];
  }
  ngOnInit() {
    super.ngOnInit();

    this.authService.getCurrentUserPermissions$().subscribe(permissions => {
      this.canShowArchivesPermission = permissions.includes(ENUM_PERMISSIONS.READ_INTERNAL_CONTACT);
    });
  }

  createItem(): void {
    this.dialog
      .open(UserProfileCreateUpdateDeleteComponent)
      .afterClosed()
      .subscribe((userProfile: UserProfileModel) => {
        if (userProfile) {
          if (userProfile._id) {
            this.router.navigateByUrl('/management/user-profile/' + userProfile._id).then();
          } else {
            this.initData();
          }
        }
      });
  }

  updateItem(element: UserProfileModel): void {
    this.dialog
      .open(UserProfileCreateUpdateDeleteComponent, {
        data: {
          defaults: element,
          mode: CrudModeEnum.Update,
        },
      })
      .afterClosed()
      .subscribe((userProfile: UserProfileModel) => {
        if (userProfile) {
          this.initData();
        }
      });
  }

  deleteItem(element: UserProfileModel) {
    this.dialog
      .open(UserProfileCreateUpdateDeleteComponent, {
        data: {
          defaults: element,
          name: `${element.firstName}`,
          mode: CrudModeEnum.Delete,
        },
      })
      .afterClosed()
      .subscribe((userProfile: UserProfileModel) => {
        if (userProfile) {
          this.initData();
        }
      });
  }

  archiveItem(element: UserProfileModel) {
    this.dialog
      .open(UserProfileCreateUpdateDeleteComponent, {
        data: {
          defaults: element,
          name: `${element.firstName}`,
          mode: CrudModeEnum.Archive,
        },
      })
      .afterClosed()
      .subscribe((userProfile: UserProfileModel) => {
        if (userProfile) {
          this.initData();
        }
      });
  }
  importItem() {
    this.dialog
      .open(UserProfileCreateUpdateDeleteComponent, {
        data: {
          mode: CrudModeEnum.Import,
        },
      })
      .afterClosed()
      .subscribe((userProfile: UserProfileModel) => {
        if (userProfile) {
          this.initData();
        }
      });
  }

  exportItem() {
    this.service.exportUserProfiles().subscribe((x: any) => {
      const b = b64ToBlob(x.data.b64, ContentTypeEnum.excel);
      this.fileSaver.save(b, x.data.fileName);
    });
  }

  protected override transformData(data: any[]): any[] {
    return data.map((user: UserProfileModel & { mobileNumber?: string }) => {
      let numbers: string;
      if (user.phoneNumbers && user.phoneNumbers.length > 0) {
        numbers = user.phoneNumbers
          .map((n: PhoneNumberModel) => n.displayPhoneNumber || `${n.prefix?.dialCode}${n.phoneNumber}`)
          .join(', ');
      } else {
        numbers = user.mobileNumber || '-';
      }
      (user as any).mobileNumber = numbers;

      (user as any).contactType = user.contactType?.length
        ? user.contactType
            .map(type => (type as any).label ?? type._id?.label)
            .filter(Boolean)
            .join(', ') || '-'
        : '-';

      return user;
    });
  }
}
